import api from './axiosInstance';
import type {
    ApplyCommitBody,
    ApplyCommitResult,
    ApplyPreview,
    ApplyPreviewBody,
    AutomatchCommitBody,
    AutomatchCommitResult,
    AutomatchPreview,
    CapacityBasis,
    EpcProjectCategory,
    ProposalAcceptResult,
    PublishStandardResult,
    RollingPlanConfig,
    RollingProposal,
    StandardApplyResolveResult,
    StandardApplyRun,
    StandardListRow,
    StandardSchedule,
    StandardStatus,
    StandardTask,
    StandardVersion,
} from '../features/epc/standards/types';

const CATEGORIES_BASE = '/epc/project-categories';
const STANDARDS_BASE = '/epc/standard-schedules';

// ---------------------------------------------------------------------------
// Project categories
// ---------------------------------------------------------------------------

/** 404 = feature flag off on the backend — normalized to [] so consumers degrade gracefully. */
export async function listProjectCategories(params: {
    projectTypeId?: string;
    includeInactive?: boolean;
}): Promise<EpcProjectCategory[]> {
    try {
        const r = await api.get(CATEGORIES_BASE, { params });
        return r.data.data.categories;
    } catch (e) {
        // NOT isAxiosError: the axios interceptor rewraps 4xx envelopes as plain
        // Errors that keep `.response` — check the status structurally instead.
        const status = (e as { response?: { status?: number } } | undefined)?.response?.status;
        if (status === 404) return [];
        throw e;
    }
}

export async function createProjectCategory(input: {
    projectTypeId: string;
    key: string;
    label: string;
    description?: string;
    sequence?: number;
}): Promise<EpcProjectCategory> {
    const r = await api.post(CATEGORIES_BASE, input);
    return r.data.data.category;
}

export async function updateProjectCategory(
    categoryId: string,
    input: {
        label?: string;
        description?: string;
        sequence?: number;
        active?: boolean;
    }
): Promise<EpcProjectCategory> {
    const r = await api.patch(`${CATEGORIES_BASE}/${categoryId}`, input);
    return r.data.data.category;
}

export async function deactivateProjectCategory(categoryId: string): Promise<EpcProjectCategory> {
    const r = await api.delete(`${CATEGORIES_BASE}/${categoryId}`);
    return r.data.data.category;
}

// ---------------------------------------------------------------------------
// Standard schedules
// ---------------------------------------------------------------------------

export async function listStandardSchedules(filters: {
    projectTypeId?: string;
    projectCategoryId?: string;
    status?: StandardStatus;
    includeArchived?: boolean;
}): Promise<StandardListRow[]> {
    const r = await api.get(STANDARDS_BASE, { params: filters });
    return r.data.data.standards;
}

export async function createStandardSchedule(input: {
    name: string;
    description?: string;
    projectTypeId: string;
    projectCategoryId: string;
    capacityBasisDefault?: CapacityBasis;
}): Promise<StandardSchedule> {
    const r = await api.post(STANDARDS_BASE, input);
    return r.data.data.standard;
}

export async function getStandardSchedule(standardId: string): Promise<StandardSchedule> {
    const r = await api.get(`${STANDARDS_BASE}/${standardId}`);
    return r.data.data.standard;
}

export async function updateStandardSchedule(
    standardId: string,
    input: {
        name?: string;
        description?: string;
        capacityBasisDefault?: CapacityBasis;
        draftTasks?: StandardTask[];
    }
): Promise<StandardSchedule> {
    const r = await api.patch(`${STANDARDS_BASE}/${standardId}`, input);
    return r.data.data.standard;
}

export async function archiveStandardSchedule(
    standardId: string,
    force = false
): Promise<StandardSchedule> {
    const r = await api.delete(`${STANDARDS_BASE}/${standardId}`, {
        params: force ? { force: true } : {},
    });
    return r.data.data.standard;
}

export async function publishStandardSchedule(
    standardId: string,
    changeNote?: string
): Promise<PublishStandardResult> {
    const r = await api.post(
        `${STANDARDS_BASE}/${standardId}/publish`,
        changeNote ? { changeNote } : {}
    );
    return r.data.data;
}

export async function listStandardVersions(standardId: string): Promise<StandardVersion[]> {
    const r = await api.get(`${STANDARDS_BASE}/${standardId}/versions`);
    return r.data.data.versions;
}

export async function getStandardVersion(
    standardId: string,
    version: number
): Promise<StandardVersion> {
    const r = await api.get(`${STANDARDS_BASE}/${standardId}/versions/${version}`);
    return r.data.data.version;
}

// ---------------------------------------------------------------------------
// Standard-apply (SP2)
// ---------------------------------------------------------------------------

const applyBase = (projectId: string): string => `/epc/projects/${projectId}/standard-apply`;

/** Which published standard the project's category/capacity resolves to (or null + reason). */
export async function resolveStandardApply(
    projectId: string
): Promise<StandardApplyResolveResult> {
    const r = await api.get(`${applyBase(projectId)}/resolve`);
    return r.data.data;
}

/** Pure preview (no writes) — assembled options → dated tree + blocks/warnings + applyId. */
export async function previewStandardApply(
    projectId: string,
    body: ApplyPreviewBody
): Promise<ApplyPreview> {
    const r = await api.post(`${applyBase(projectId)}/preview`, body);
    return r.data.data.preview;
}

/** Commit — the full preview inputs + applyId (server recomputes the applyId to gate). */
export async function commitStandardApply(
    projectId: string,
    body: ApplyCommitBody
): Promise<ApplyCommitResult> {
    const r = await api.post(`${applyBase(projectId)}/commit`, body);
    return r.data.data;
}

export async function listStandardApplyRuns(projectId: string): Promise<StandardApplyRun[]> {
    const r = await api.get(`${applyBase(projectId)}/runs`);
    return r.data.data.runs;
}

export async function getStandardApplyRun(
    projectId: string,
    runId: string
): Promise<StandardApplyRun> {
    const r = await api.get(`${applyBase(projectId)}/runs/${runId}`);
    return r.data.data.run;
}

// ---------------------------------------------------------------------------
// Auto-match (SP3)
// ---------------------------------------------------------------------------

const automatchBase = (projectId: string): string => `/epc/projects/${projectId}/automatch`;

/** Pure preview (no writes) — score imported tasks against the standard leaves + automatchId. */
export async function previewAutomatch(
    projectId: string,
    body: { importId?: string }
): Promise<AutomatchPreview> {
    const r = await api.post(`${automatchBase(projectId)}/preview`, body);
    return r.data.data.preview;
}

/** Commit — automatchId gate + per-row acceptance (server re-derives to gate). */
export async function commitAutomatch(
    projectId: string,
    body: AutomatchCommitBody
): Promise<AutomatchCommitResult> {
    const r = await api.post(`${automatchBase(projectId)}/commit`, body);
    return r.data.data;
}

// ---------------------------------------------------------------------------
// Rolling adaptive engine (SP4)
// ---------------------------------------------------------------------------

const rollingBase = (projectId: string): string => `/epc/projects/${projectId}/rolling-plan`;

/** Current rolling config (or the implicit `off` default when unset). */
export async function getRollingConfig(projectId: string): Promise<RollingPlanConfig> {
    const r = await api.get(`${rollingBase(projectId)}/config`);
    return r.data.data.config;
}

/** Upsert the rolling config. */
export async function putRollingConfig(
    projectId: string,
    config: RollingPlanConfig
): Promise<RollingPlanConfig> {
    const r = await api.put(`${rollingBase(projectId)}/config`, config);
    return r.data.data.config;
}

/** Enqueue a manual roll run (202). Returns the run id. */
export async function rollNow(projectId: string): Promise<{ rollRunId: string }> {
    const r = await api.post(`${rollingBase(projectId)}/roll-now`, {});
    return r.data.data;
}

/** Pending (default) or filtered proposals for the project. */
export async function listRollingProposals(
    projectId: string,
    status = 'pending'
): Promise<RollingProposal[]> {
    const r = await api.get(`${rollingBase(projectId)}/proposals`, { params: { status } });
    return r.data.data.proposals;
}

export async function getRollingProposal(
    projectId: string,
    proposalId: string
): Promise<RollingProposal> {
    const r = await api.get(`${rollingBase(projectId)}/proposals/${proposalId}`);
    return r.data.data.proposal;
}

/** Accept (all) or partially-accept (`dates`) a proposal — 409 ROLL_STALE if actuals moved. */
export async function acceptRollingProposal(
    projectId: string,
    proposalId: string,
    body: { dates?: string[] }
): Promise<ProposalAcceptResult> {
    const r = await api.post(`${rollingBase(projectId)}/proposals/${proposalId}/accept`, body);
    return r.data.data;
}

export async function rejectRollingProposal(
    projectId: string,
    proposalId: string,
    body: { note?: string }
): Promise<RollingProposal> {
    const r = await api.post(`${rollingBase(projectId)}/proposals/${proposalId}/reject`, body);
    return r.data.data.proposal;
}

/** Feature B result — variance always present; proposal iff status === 'proposal'. */
export interface RollTaskNowResult {
    status: 'proposal' | 'on_track' | 'ahead';
    variance: RollingProposal['variance'];
    proposal?: RollingProposal;
}

/** Per-task forced roll (Feature B) — synchronous; bypasses hash-skip + damping (D2). */
export async function rollTaskNow(projectId: string, taskKey: string): Promise<RollTaskNowResult> {
    const r = await api.post(
        `${rollingBase(projectId)}/tasks/${encodeURIComponent(taskKey)}/roll`,
        {}
    );
    return r.data.data;
}
